import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import type { TeamPickerPerson } from "work/work-agreements/teams/people-picker/team-picker-person";

/**
 * What the picker is choosing: people for the team, any of whom may be made its leader, or the
 * leader alone ("Add leader…"), where choosing somebody replaces whoever was chosen before.
 */
export type TeamPeoplePickerMode = "people" | "leader";

/** How much of a team or company the selection holds: drawn as an empty, mixed or full checkbox. */
export type GroupSelectionState = "none" | "some" | "all";

/**
 * What the picker hands the roster when the user presses "Add": the people to put on the team, and
 * the one of them to lead it, if any. One value rather than two calls, because the rosters build each
 * change on the team they rendered, and a leader change followed by a member change would have the
 * second undo the first.
 */
export type TeamPeopleChange = {
  leader?: EntityMember;
  members: EntityMember[];
  /**
   * When `leader` replaces somebody, whether that person stays on the team as a member. Never
   * assumed: a leader may lack the credentials a member needs (#5800), so the picker asks, and a
   * change that does not say takes them off the team.
   */
  formerLeaderStays?: boolean;
};

function toEntityMember(person: TeamPickerPerson): EntityMember {
  return new EntityMember({ userId: new Guid(person.userId), legalEntityId: new Guid(person.legalEntityId) });
}

/**
 * The people chosen in the team people picker, in the order they were chosen, and which of them is
 * to lead the team (#6602).
 *
 * Immutable: every change answers a new selection, so a component keeps it in state as it is. The
 * rules it keeps are the picker's whole contract — a person is chosen once however many sources offer
 * them, there is at most one leader, and the leader is one of the people chosen — and each is a spec
 * of its own rather than a branch inside a view.
 */
export default class TeamPeopleSelection {
  public readonly mode: TeamPeoplePickerMode;
  public readonly people: readonly TeamPickerPerson[];
  public readonly leaderUserId?: string;

  public get count(): number {
    return this.people.length;
  }

  public get leader(): TeamPickerPerson | undefined {
    return this.people.find((person) => person.userId === this.leaderUserId);
  }

  public constructor(mode: TeamPeoplePickerMode, people: readonly TeamPickerPerson[] = [], leaderUserId?: string) {
    this.mode = mode;
    this.people = people;
    this.leaderUserId = people.some((person) => person.userId === leaderUserId) ? leaderUserId : undefined;
  }

  public has(userId: string): boolean {
    return this.people.some((person) => person.userId === userId);
  }

  public isLeader(userId: string): boolean {
    return this.leaderUserId === userId;
  }

  /**
   * Chooses the person, or lets them go if they were chosen. Choosing somebody in leader mode makes
   * them the leader and lets go of whoever was chosen before: that mode picks one person.
   */
  public toggle(person: TeamPickerPerson): TeamPeopleSelection {
    if (this.has(person.userId)) return this.remove([person.userId]);
    if (this.mode === "leader") return new TeamPeopleSelection(this.mode, [person], person.userId);
    return new TeamPeopleSelection(this.mode, [...this.people, person], this.leaderUserId);
  }

  /** Chooses every one of `people` not already chosen. Never in leader mode, which picks one person. */
  public add(people: readonly TeamPickerPerson[]): TeamPeopleSelection {
    if (this.mode === "leader") return this;
    const additions = people.filter(
      (person, index) => !this.has(person.userId) && people.findIndex((other) => other.userId === person.userId) === index,
    );
    if (additions.length === 0) return this;
    return new TeamPeopleSelection(this.mode, [...this.people, ...additions], this.leaderUserId);
  }

  /** Lets go of these people. Letting go of the leader leaves the selection without one. */
  public remove(userIds: readonly string[]): TeamPeopleSelection {
    const kept = this.people.filter((person) => !userIds.includes(person.userId));
    if (kept.length === this.people.length) return this;
    return new TeamPeopleSelection(this.mode, kept, this.leaderUserId);
  }

  /**
   * How much of a group the selection holds, counting only the people in it who can be chosen. A
   * group none of whose people can be chosen reads as empty.
   */
  public groupState(people: readonly TeamPickerPerson[], canChoose: (person: TeamPickerPerson) => boolean): GroupSelectionState {
    const choosable = people.filter(canChoose);
    const chosen = choosable.filter((person) => this.has(person.userId)).length;
    if (chosen === 0) return "none";
    return chosen === choosable.length ? "all" : "some";
  }

  /**
   * The group row's checkbox: a group that is wholly chosen is let go, and any other is wholly chosen.
   * People who cannot be chosen are left as they are either way.
   */
  public toggleGroup(people: readonly TeamPickerPerson[], canChoose: (person: TeamPickerPerson) => boolean): TeamPeopleSelection {
    const choosable = people.filter(canChoose);
    if (this.groupState(people, canChoose) === "all") return this.remove(choosable.map((person) => person.userId));
    return this.add(choosable);
  }

  /** Makes this chosen person the leader. There is one leader, so whoever led before becomes a member. */
  public makeLeader(userId: string): TeamPeopleSelection {
    if (!this.has(userId) || this.isLeader(userId)) return this;
    return new TeamPeopleSelection(this.mode, this.people, userId);
  }

  /** Makes this chosen person a member. In leader mode there is nothing else for them to be. */
  public makeMember(userId: string): TeamPeopleSelection {
    if (!this.isLeader(userId) || this.mode === "leader") return this;
    return new TeamPeopleSelection(this.mode, this.people, undefined);
  }

  public clear(): TeamPeopleSelection {
    return new TeamPeopleSelection(this.mode);
  }

  public toChange(): TeamPeopleChange {
    const leader = this.leader;
    return {
      leader: leader ? toEntityMember(leader) : undefined,
      members: this.people.filter((person) => person !== leader).map(toEntityMember),
    };
  }
}
